/**
 * Draws non-LOD splats with SuperSplat's renderer (projected-splat-renderer.ts,
 * ported from the 3.1.2 editor) instead of the engine's unified gsplat path,
 * so a scene looks in the viewer exactly as it does in the editor.
 *
 * The engine still loads the asset; the gsplat component is disabled so the
 * engine does not draw it, and the ported renderer projects, sorts and draws
 * from an uncompressed GSplatResource (SOG / compressed PLY are decompressed
 * once on load). Enabled with ?sr=1 (see app.ts).
 */

import {
    LAYERID_WORLD,
    type AppBase,
    type Asset,
    type Entity,
    type GSplatData,
    GSplatResource,
    type GSplatResourceBase
} from 'playcanvas';

import { ProjectedSplatRenderer } from './projected-splat-renderer';
import { Events, type Scene } from './types';
import { ViewerSplat } from './viewer-splat';

class SceneShim implements Scene {
    app: AppBase;
    events = new Events();
    camera: Scene['camera'];
    splatLayer: { id: number };
    movingRender = false;

    constructor(app: AppBase, cameraEntity: Entity) {
        this.app = app;
        this.camera = { camera: cameraEntity.camera!, mainCamera: cameraEntity, renderOverlays: false };
        this.splatLayer = { id: app.scene.layers.getLayerById(LAYERID_WORLD)?.id ?? LAYERID_WORLD };
    }

    get graphicsDevice() {
        return this.app.graphicsDevice;
    }

    // the renderer sizes its viewport from this every frame
    get targetSize() {
        const gd = this.app.graphicsDevice;
        return { width: gd.width, height: gd.height };
    }
}

// the editor state the renderer asks for, at its neutral values
const NEUTRAL: Record<string, unknown> = {
    'view.bands': 3,
    'view.minPixelSize': 0,
    'view.editView': false,
    'view.gaussians': true,
    'view.selectionColor': false,
    'view.rings': false,
    'view.selectionRings': false,
    'view.ringSize': 0,
    'view.ringsColorBlend': 0,
    'view.ringsSelectionBlend': 0,
    'view.splatsColorBlend': 0,
    'view.splatsSelectionBlend': 0,
    'view.outlineSelection': false,
    'selection': null,
    'selection.footprint': 1,
    'selectedClr': { r: 1, g: 1, b: 0, a: 1 },
    'unselectedClr': { r: 0, g: 0, b: 1, a: 0.5 },
    'lockedClr': { r: 0, g: 0, b: 0, a: 0.05 },
    'colorPanel.pending': null
};

type DataWithDecompress = { decompress?: () => GSplatData | Promise<GSplatData> };

class ViewerSplatRenderer {
    readonly scene: SceneShim;
    readonly renderer: ProjectedSplatRenderer;
    private readonly splats = new Map<Entity, ViewerSplat>();
    private readonly onUpdate: () => void;

    constructor(app: AppBase, cameraEntity: Entity) {
        this.scene = new SceneShim(app, cameraEntity);
        for (const [name, value] of Object.entries(NEUTRAL)) {
            this.scene.events.function(name, () => value);
        }
        this.renderer = new ProjectedSplatRenderer(this.scene);
        // project + sort before the frame renders, as the editor does from its update
        this.onUpdate = () => this.renderer.render();
        app.on('update', this.onUpdate);
    }

    /** Spherical-harmonic bands to evaluate (0-3). */
    set bands(value: number) {
        this.scene.events.functions.set('view.bands', () => value);
    }

    /** Cull splats whose projected size is under this many pixels (0 = never). */
    set minPixelSize(value: number) {
        this.scene.events.functions.set('view.minPixelSize', () => value);
    }

    /** Take over drawing of a loaded gsplat entity. */
    async addEntity(entity: Entity, asset: Asset): Promise<void> {
        const device = this.scene.graphicsDevice;
        const base = asset.resource as GSplatResourceBase;
        const data = base.gsplatData as unknown as DataWithDecompress;
        let resource: GSplatResource;
        let owns = false;
        if (base instanceof GSplatResource) {
            // uncompressed ply: the engine's resource already has the textures
            resource = base;
        } else {
            const plain = typeof data.decompress === 'function' ? await data.decompress() : (base.gsplatData as GSplatData);
            resource = new GSplatResource(device, plain);
            owns = true;
        }
        // the engine must not draw it too
        if (entity.gsplat) entity.gsplat.enabled = false;
        const splat = new ViewerSplat(device, entity, resource, owns);
        this.splats.set(entity, splat);
        this.renderer.add(splat);
        console.log(`[viewer2] SuperSplat renderer: ${resource.numSplats} splats, ${resource.shBands} SH bands (${owns ? 'decompressed' : 'shared'})`);
    }

    removeEntity(entity: Entity) {
        const splat = this.splats.get(entity);
        if (!splat) return;
        this.renderer.remove(splat);
        splat.destroy();
        this.splats.delete(entity);
    }

    get stats() {
        return this.renderer.stats;
    }

    destroy() {
        this.scene.app.off('update', this.onUpdate);
        for (const entity of [...this.splats.keys()]) this.removeEntity(entity);
        this.renderer.destroy();
    }
}

export { ViewerSplatRenderer };
